import { faPhone, faEnvelope } from '@fortawesome/pro-regular-svg-icons';
import { faDownload, faExternalLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ComponentPropsWithoutRef, FC } from 'react';
import { useMemo } from 'react';
import type { LinkProps as RouterLinkProps } from 'react-router-dom';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import type { ActionMode } from 'src/components/action-mode';
import { useActionMode } from 'src/components/action-mode';
import { useActionGroupMode } from 'src/components/buttons-and-actions/action-group';
import {
  getActionGroupModeClasses,
  getBaseButtonClasses,
  getButtonDropdownClasses,
  getButtonInverseClasses,
  getButtonPrimaryClasses,
  getButtonSecondaryClasses,
  type Size,
} from 'src/components/buttons-and-actions/button';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { cn } from 'src/utils/cn';
import type { StrictOmit } from 'src/utils/utility-types';

export type BaseLinkProps =
  | {
      mode?:
        | 'default'
        | 'default-underlined'
        | 'inverse'
        | 'inverse-underlined'
        | 'button-inverse' // maybe we can delete this when we delete the legacy styles (only used in menu)
        | 'dropdown';
      size?: undefined;
      /** This mode doesn't use `isActive`, but you can pass it, in case mode is switched dynamically. */
      isActive?: boolean;
      hideIcon?: undefined;
      /** This mode doesn't use `disabled`, but you can pass it, in case mode is switched dynamically. */
      disabled?: boolean;
    }
  | {
      mode?: 'default-underlined';
      size?: undefined;
      isActive?: undefined;
      /**
       * This is only relevant for external links. *Very* carefully use this.
       * E.g. if we point to an external site belonging to Prisma. This icon is meant
       * to be an indicator that the user is redirected to a 3rd party site.
       */
      hideIcon?: boolean;
      disabled?: undefined;
    }
  | {
      mode: 'primary' | 'secondary' | 'pill';
      size?: undefined;
      isActive?: boolean;
      hideIcon?: undefined;
      disabled?: undefined;
    }
  | {
      mode: 'sidebar';
      size?: undefined;
      isActive?: boolean;
      hideIcon?: undefined;
      disabled?: boolean; // TODO: check if still needed (this was never really used for disabling the link)
    }
  | {
      mode: 'button-primary';
      size?: Size;
      /** This mode doesn't use `isActive`, but you can pass it, in case mode is switched dynamically. */
      isActive?: boolean;
      hideIcon?: undefined;
      disabled?: undefined;
    }
  | {
      mode: 'button-secondary';
      size?: Size;
      isActive?: boolean;
      hideIcon?: undefined;
      disabled?: undefined;
    }
  | {
      mode: 'quick-filter';
      size?: undefined;
      isActive?: boolean;
      hideIcon?: undefined;
      disabled?: undefined;
    }
  | {
      mode: 'dropdown';
      size?: undefined;
      isActive?: boolean;
      hideIcon?: undefined;
      disabled?: boolean;
    };

const routeAwareActiveness: BaseLinkProps['mode'][] = [
  'primary',
  'secondary',
  'pill',
  'dropdown',
  'sidebar',
];

export type LinkProps<S> = BaseLinkProps &
  StrictOmit<RouterLinkProps, 'state'> & { state?: S };

export const Link: FC<LinkProps<any>> = (baseProps) => {
  const actionMode = useActionMode();
  const { pathname } = useLocation();
  const actionGroupMode = useActionGroupMode();
  const isNewDesignActive = useIsNewDesignActive();

  // descruct all custom props
  const {
    mode,
    size,
    isActive: explicitIsActive,
    hideIcon,
    disabled,
    ...props
  } = {
    ...mapActionMode(actionMode),
    ...baseProps,
  } as LinkProps<any>;

  // can be overwritten by passing `isActive` explicitly
  const isActive = useMemo(() => {
    if (explicitIsActive !== undefined) return explicitIsActive;
    if (!routeAwareActiveness.includes(mode)) return false;

    if (props.to) {
      const to =
        typeof props.to === 'string'
          ? props.to
          : typeof props.to === 'function'
            ? '' // note: function version is not supported
            : props.to.pathname || '';
      return pathname.startsWith(
        to.replace(`${PRISMA_CONFIG.angularUrl}/#`, '')
      );
    } else {
      return false;
    }
  }, [pathname, props.to, explicitIsActive, mode]);

  switch (mode) {
    case 'primary':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            `
              text-[1.6rem] text-white
              hover:border-b-white hover:text-white
              focus:border-b-white focus:text-white
            `,
            isActive && 'border-b-white text-white',
            props.className
          )}
        />
      );
    case 'secondary':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            `
              text-[1.6rem] text-legacy-brand
              hover:border-b-legacy-brand hover:text-legacy-brand
              focus:border-b-legacy-brand focus:text-legacy-brand
            `,
            isActive && 'border-b-legacy-brand text-legacy-brand',
            props.className
          )}
        />
      );
    case 'pill':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            `
              text-[1.6rem] text-legacy-brand
              hover:border-b-legacy-brand hover:text-legacy-brand
              focus:border-b-legacy-brand focus:text-legacy-brand
            `,
            isActive && 'border-b-legacy-brand text-legacy-brand',
            props.className
          )}
        />
      );
    case 'inverse':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            `
              text-white
              hover:text-legacy-brand-secondary
              focus:text-legacy-brand-secondary
            `,
            props.className
          )}
        />
      );
    case 'inverse-underlined':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            `
              border-b-white text-white
              hover:text-legacy-brand-secondary
              focus:text-legacy-brand-secondary
            `,
            props.className
          )}
        />
      );
    case 'dropdown':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          // className={cn(
          //   getBaseLinkStyles({ isActive }),
          //   'block px-[2rem] py-[0.2rem] text-[#333]',
          //   isActive && '[&>span]:border-b-[0.2rem] [&>span]:border-[#333]',
          //   !disabled &&
          //     `
          //       hover:bg-[#f5f5f5] hover:text-[#333]
          //       focus:bg-[#f5f5f5] focus:text-[#333]
          //     `,
          //   disabled && 'cursor-not-allowed opacity-65',
          //   props.className
          // )}
          className={getButtonDropdownClasses({
            className: cn(
              `
                legacy:border-b-2 legacy:border-b-transparent
                legacy:leading-[1.42857143]
              `,
              props.className
            ),
          })}
        >
          {isNewDesignActive ? props.children : <span>{props.children}</span>}
        </CustomRouterLink>
      );
    case 'button-primary':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getButtonPrimaryClasses({
            actionGroupMode,
            size,
            className: props.className,
          })}
        />
      );
    case 'button-secondary':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getButtonSecondaryClasses({
            isActive,
            actionGroupMode,
            size,
            className: props.className,
          })}
        />
      );
    case 'button-inverse':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={getButtonInverseClasses({
            actionGroupMode,
            className: props.className,
          })}
        />
      );
    case 'sidebar':
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            'block px-[1.5rem] py-[0.5rem] text-[1.7rem] text-legacy-brand',
            isActive &&
              '[&>span]:border-b-[0.2rem] [&>span]:border-legacy-brand',
            `
              hover:[&>span]:border-b-[0.2rem]
              hover:[&>span]:border-legacy-brand
              focus:[&>span]:border-b-[0.2rem]
              focus:[&>span]:border-legacy-brand
            `,
            props.className
          )}
        >
          <span>{props.children}</span>
        </CustomRouterLink>
      );
    case 'quick-filter':
      if (isActive) {
        return (
          <div
            className={cn(
              getBaseLinkStyles({ isActive }),
              getActionGroupModeClasses({ actionGroupMode }),
              `
                border-[0.1rem] border-legacy-brand-light-3
                bg-legacy-brand-light-4 px-[2rem] py-[0.6rem] text-legacy-brand
                shadow-[inset_0.1rem_0.1rem_0.4rem_rgba(0,0,0,0.25)] select-none
              `,
              props.className
            )}
          >
            {props.children}
          </div>
        );
      }
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            getActionGroupModeClasses({ actionGroupMode }),
            `
              border-[0.1rem] border-legacy-brand-light-3
              bg-legacy-brand-light-4 px-[2rem] py-[0.6rem] text-legacy-brand
              hover:border-legacy-brand-secondary
              hover:bg-legacy-brand-secondary hover:text-white
              focus:border-legacy-brand-secondary
              focus:bg-legacy-brand-secondary focus:text-white
              active:shadow-[inset_0.1rem_0.1rem_0.4rem_rgba(0,0,0,0.25)]
            `,
            props.className
          )}
        />
      );
    case 'default':
    case 'default-underlined':
    case undefined:
      return (
        <CustomRouterLink
          disabled={disabled}
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            'text-legacy-brand',
            mode === 'default-underlined' &&
              'border-b-0 underline decoration-legacy-brand-light-2',
            `
              hover:text-legacy-brand-secondary
              focus:text-legacy-brand-secondary
            `,
            props.className
          )}
        />
      );
  }
};

const disabledLinkProps: ComponentPropsWithoutRef<'a'> & {
  'data-force-disabled': true;
} = {
  href: undefined,
  role: 'link',
  'aria-disabled': true,
  'data-force-disabled': true,
};

export type ExternalLinkProps = BaseLinkProps &
  ComponentPropsWithoutRef<'a'> & {
    /**
     * This is needed if we do NOT want to convert an internal link from
     * <ExternalLink/> to <Link/>, because we WANT the refresh.
     */
    forceRefresh?: boolean;
  };

export const ExternalLink: FC<ExternalLinkProps> = ({
  disabled,
  ...baseProps
}) => {
  const actionMode = useActionMode();
  const { pathname, hash } = useLocation();
  const actionGroupMode = useActionGroupMode();
  const isNewDesignActive = useIsNewDesignActive();

  // see https://web.dev/external-anchors-use-rel-noopener
  const unsafeCrossOriginDestination =
    baseProps.target === '_blank' && !baseProps.rel;

  // descruct all custom props
  const {
    mode,
    size,
    isActive: explicitIsActive,
    hideIcon,
    forceRefresh,
    ...props
  } = {
    ...mapActionMode(actionMode),
    ...baseProps,
    ...(unsafeCrossOriginDestination && { rel: 'noopener noreferrer' }),
    ...(disabled && disabledLinkProps),
  } as ExternalLinkProps;

  // can be overwritten by passing `isActive` explicitly
  const isActive = useMemo(() => {
    if (explicitIsActive !== undefined) return explicitIsActive;
    if (!routeAwareActiveness.includes(mode)) return false;

    if (props.href) {
      const cleanedHref = props.href.replace(
        `${PRISMA_CONFIG.angularUrl}/#`,
        ''
      );
      return pathname === '/platform' || pathname === '/platform/'
        ? hash.startsWith(`#${cleanedHref}`)
        : pathname.startsWith(cleanedHref);
    } else {
      return false;
    }
  }, [pathname, hash, props.href, explicitIsActive, mode]);

  // links to the old frontend used to point to a standalone Angular application
  // in the past (and therefor we used external links), but since we started to
  // embed Angular pages into React we manually convert them to internal links
  if (props.href?.startsWith(PRISMA_CONFIG.angularUrl) && !forceRefresh) {
    const to = props.href.replace(PRISMA_CONFIG.angularUrl, '/platform');
    const { href, ...baseLinkProps } = props;
    const linkProps = {
      ...baseLinkProps,
      isActive,
      to,
      mode,
      size,
      disabled,
    } as LinkProps<any>;
    return <Link {...linkProps} />;
  }

  switch (mode) {
    case 'primary':
      return (
        <a
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            `
              text-[1.6rem] text-white
              hover:border-b-white hover:text-white
              focus:border-b-white focus:text-white
            `,
            isActive && 'border-b-white text-white',
            props.className
          )}
        />
      );
    case 'secondary':
      return (
        <a
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            `
              text-[1.6rem] text-legacy-brand
              hover:border-b-legacy-brand hover:text-legacy-brand
              focus:border-b-legacy-brand focus:text-legacy-brand
            `,
            isActive && 'border-b-legacy-brand text-legacy-brand',
            props.className
          )}
        />
      );
    case 'pill':
      return (
        <a
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            `
              text-[1.6rem] text-legacy-brand
              hover:border-b-legacy-brand hover:text-legacy-brand
              focus:border-b-legacy-brand focus:text-legacy-brand
            `,
            isActive && 'border-b-legacy-brand text-legacy-brand',
            props.className
          )}
        />
      );
    case 'inverse':
      return (
        <a
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            `
              text-white
              hover:text-legacy-brand-secondary
              focus:text-legacy-brand-secondary
            `,
            props.className
          )}
        />
      );
    case 'inverse-underlined':
      return (
        <a
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            `
              border-b-white text-white
              hover:text-legacy-brand-secondary
              focus:text-legacy-brand-secondary
            `,
            props.className
          )}
        />
      );
    case 'dropdown':
      return (
        <a
          {...props}
          // className={cn(
          //   getBaseLinkStyles({ isActive }),
          //   'block px-[2rem] py-[0.2rem] text-[#333]',
          //   isActive && '[&>span]:border-b-[0.2rem] [&>span]:border-[#333]',
          //   !disabled &&
          //     `
          //       hover:bg-[#f5f5f5] hover:text-[#333]
          //       focus:bg-[#f5f5f5] focus:text-[#333]
          //     `,
          //   disabled && 'cursor-not-allowed opacity-65',
          //   props.className
          // )}
          className={getButtonDropdownClasses({
            className: cn(
              `
                legacy:border-b-2 legacy:border-b-transparent
                legacy:leading-[1.42857143]
              `,
              props.className
            ),
          })}
        >
          {isNewDesignActive ? props.children : <span>{props.children}</span>}
        </a>
      );
    case 'button-primary':
      return (
        <a
          {...props}
          className={getButtonPrimaryClasses({
            actionGroupMode,
            size,
            className: props.className,
          })}
        />
      );
    case 'button-secondary':
      return (
        <a
          {...props}
          className={getButtonSecondaryClasses({
            isActive,
            actionGroupMode,
            size,
            className: props.className,
          })}
        />
      );
    case 'button-inverse':
      return (
        <a
          {...props}
          className={getButtonInverseClasses({
            actionGroupMode,
            className: props.className,
          })}
        />
      );
    case 'sidebar':
      return (
        <a
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            'block px-[1.5rem] py-[0.5rem] text-[1.7rem] text-legacy-brand',
            isActive &&
              '[&>span]:border-b-[0.2rem] [&>span]:border-legacy-brand',
            `
              hover:[&>span]:border-b-[0.2rem]
              hover:[&>span]:border-legacy-brand
              focus:[&>span]:border-b-[0.2rem]
              focus:[&>span]:border-legacy-brand
            `,
            props.className
          )}
        >
          <span>{props.children}</span>
        </a>
      );
    case 'quick-filter':
      if (isActive) {
        return (
          <div
            className={cn(
              getBaseLinkStyles({ isActive }),
              getActionGroupModeClasses({ actionGroupMode }),
              `
                border-[0.1rem] border-legacy-brand-light-3
                bg-legacy-brand-light-4 px-[2rem] py-[0.6rem] text-legacy-brand
                shadow-[inset_0.1rem_0.1rem_0.4rem_rgba(0,0,0,0.25)] select-none
              `,
              props.className
            )}
          >
            {props.children}
          </div>
        );
      }
      return (
        <a
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            getActionGroupModeClasses({ actionGroupMode }),
            `
              border-[0.1rem] border-legacy-brand-light-3
              bg-legacy-brand-light-4 px-[2rem] py-[0.6rem] text-legacy-brand
              hover:border-legacy-brand-secondary
              hover:bg-legacy-brand-secondary hover:text-white
              focus:border-legacy-brand-secondary
              focus:bg-legacy-brand-secondary focus:text-white
              active:shadow-[inset_0.1rem_0.1rem_0.4rem_rgba(0,0,0,0.25)]
            `,
            props.className
          )}
        />
      );
    case 'default-underlined':
      return (
        <span>
          <a
            {...props}
            className={cn(
              getBaseLinkStyles({ isActive }),
              `
                border-b-0 text-legacy-brand underline
                decoration-legacy-brand-light-2
                hover:text-legacy-brand-secondary
                focus:text-legacy-brand-secondary
              `,
              props.className
            )}
          />
          {!hideIcon && (
            <>
              {' '}
              <FontAwesomeIcon
                icon={
                  props.download || isFileUri(props.href)
                    ? faDownload
                    : props.href?.startsWith('tel:')
                      ? faPhone
                      : props.href?.startsWith('mailto:')
                        ? faEnvelope
                        : faExternalLink
                }
              />
            </>
          )}
        </span>
      );
    case 'default':
    case undefined:
      return (
        <a
          {...props}
          className={cn(
            getBaseLinkStyles({ isActive }),
            `
              text-legacy-brand
              hover:text-legacy-brand-secondary
              focus:text-legacy-brand-secondary
            `,
            props.className
          )}
        />
      );
  }
};

// allow "disabled" state
const CustomRouterLink: FC<
  { disabled?: boolean } & RouterLinkProps &
    StrictOmit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>
> = ({
  // custom props
  disabled,
  // router link props
  reloadDocument,
  replace,
  state,
  preventScrollReset,
  relative,
  to,
  viewTransition,
  // native props
  ...rest
}) => {
  if (disabled) {
    return <a {...rest} {...disabledLinkProps} />;
  }

  return (
    <RouterLink
      {...rest}
      reloadDocument={reloadDocument}
      replace={replace}
      state={state}
      preventScrollReset={preventScrollReset}
      relative={relative}
      to={to}
      viewTransition={viewTransition}
    />
  );
};

function getBaseLinkStyles({ isActive }: { isActive?: boolean }) {
  return cn(
    getBaseButtonClasses({ isActive }),
    'border-b-[0.2rem] border-transparent no-underline'
  );
}

function isFileUri(uri?: string) {
  if (!uri) return false;
  const url = new URL(uri);
  return Boolean(/\/[^/]+\.[^/]+$/.test(url.pathname));
}

function mapActionMode(actionMode: ActionMode | null): BaseLinkProps | null {
  if (!actionMode) return null;
  if (actionMode.mode === 'link-primary') {
    return { ...actionMode, mode: 'primary' as const };
  }
  return actionMode as BaseLinkProps;
}
